import { useMutation, useQuery } from '@apollo/client';
import { Status } from '@teamsnap/snap-ui';
import {
  FilterContext,
  PaginatedTable,
  PanelRow,
  PopupTooltip,
  Tag,
} from '@teamsnap/teamsnap-ui';
import { Placement } from '@teamsnap/teamsnap-ui/dist/js/types/placement';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import * as React from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

// Local imports
import { ActionType } from '@teamsnap/snap-ui/build/components/ActionMenu/ActionMenu.types';
import { ActionColumn } from 'components/common/action-column';
import ENVIRONMENT from 'core/environment';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import { DateFormat, DateService, FeatureFlagService } from 'frontend-toolkit';
import { TEAMSNAP_URL } from '../../core/constants';
import * as GqlDuplicateForm from '../../core/gql/mutations/duplicate-form-reg1.0';
import * as GqlUpdateRetired from '../../core/gql/mutations/update-retired-for-reg2-form';
import * as GqlForms from '../../core/gql/queries/get-all-forms-reg';
import FormModal, { FormOperation } from './FormModal';
import './Registration.scss';
import {
  applyFilters,
  applySort,
  extractFilterValues,
  generateFormUrl,
  handleExport,
} from './utils';
import { DuplicateReg2FormModal } from './v2/DuplicateReg2Form';
import ShareFormDrawer from './v2/ShareFormDrawer';

export enum FormRetiredStatus {
  Active = 'active',
  Retired = 'retired',
}

export interface Props {
  orgId: number;
  programId?: number;
  seasonId?: number;
  orgName: string;
  retiredStatus: FormRetiredStatus;
  setIsModalOpen?: (boolean) => void;
}

export interface FormProgram {
  __typename: string;
  id: number;
  name: string;
}

export interface FormSeason {
  __typename: string;
  id: number;
  name: string;
  tsDivisionId: string;
  program?: FormProgram;
}

export interface Form {
  __typename: string;
  closeAt: string;
  openAt: string;
  createdAt: string;
  id: number;
  name: string;
  isOpen: boolean;
  isRetired: boolean;
  isReg2: boolean;
  season?: FormSeason;
  participantCount: number;
  registeredTodayCount: number;
  collected: number;
  balance: number;
}

type Action = {
  requiresConfirmation?: boolean;
  text: string | React.ReactElement;
  callback: (x?: [React.MouseEvent<HTMLButtonElement, MouseEvent>]) => void;
  confirmationText?: string | React.ReactElement;
};

const RegistrationList: React.FC<Props> = ({
  orgId,
  programId,
  seasonId,
  orgName,
  retiredStatus,
  setIsModalOpen,
}) => {
  const history = useHistory();
  const [forms, setForms] = React.useState<Form[]>([]);
  const filteredFormsRef = React.useRef<Form[]>([]);
  const [focusedFormId, setFocusedFormId] = React.useState<number>(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] =
    React.useState<boolean>(false);
  const [isShareDrawerOpen, setIsShareDrawerOpen] = React.useState(false);
  const [duplicateReg2FormId, setDuplicateReg2FormId] =
    React.useState<string>(undefined);
  const [formIDForSharing, setFormIdForSharing] = React.useState(null);
  const [isMixedForms, setIsMixedForms] = React.useState(false);
  const [activeFilters, setActiveFilters] = React.useState({});

  const queryForms = useQuery(GqlForms.QUERY, {
    variables: { organizationId: orgId },
  });

  const [duplicateFormRequest] = useMutation(GqlDuplicateForm.MUTATION);
  const [updateRetiredFormRequest] = useMutation(GqlUpdateRetired.MUTATION, {
    refetchQueries: [
      { query: GqlForms.QUERY, variables: { organizationId: +orgId } },
    ],
  });

  const duplicateReg2FormsEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.DUPLICATE_FORM_REG_2],
    ENVIRONMENT,
    orgId || undefined
  );

  const duplicateForm = async (season, formName) => {
    try {
      const response = await duplicateFormRequest({
        variables: {
          formId: focusedFormId,
          newName: formName,
          newSeasonId: parseInt(season?.id),
        },
      });

      if (response?.data?.duplicateForm?.id) {
        window.location.href = `${TEAMSNAP_URL}/divisions/${season?.tsDivisionId}/registration/${response?.data?.duplicateForm?.id}/edit`;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const updateRetired = async (formId, retiredValue) => {
    try {
      const response = await updateRetiredFormRequest({
        variables: {
          formId: formId,
          retired: retiredValue,
        },
      });

      if (response?.data?.updateRetired?.id) {
        reactiveFeedbackMessage({
          message: `Form successully ${retiredValue ? 'retired' : 'unretired'}`,
          isError: false,
          autoclose: true,
        });
      }
    } catch (error) {
      if (error.graphQLErrors) {
        const [errors] = error.graphQLErrors;

        reactiveFeedbackMessage({
          message: errors.message,
          isError: true,
          autoclose: true,
        });
      } else {
        reactiveFeedbackMessage({
          message:
            'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
          isError: true,
          autoclose: true,
        });
      }
    }
  };

  React.useEffect(() => {
    let forms = queryForms.data?.forms ?? [];
    // Filter if season level registration
    if (orgId && programId && seasonId) {
      forms = forms.filter(
        (form) =>
          +form.season.id === +seasonId &&
          +form.season.program_id === +programId
      );
      if (forms.length > 0) {
        setActiveFilters({
          program: [forms[0].season.program.name],
          season: [forms[0].season.name],
        });
      }
    }
    setForms(forms);
  }, [queryForms.data]);

  React.useEffect(() => {
    if (queryForms.data && queryForms.data?.forms) {
      const {
        data: { forms },
      } = queryForms;

      setIsMixedForms(
        forms.some((f: Form) => f.isReg2) && forms.some((f: Form) => !f.isReg2)
      );
    }

    return () => {
      setIsMixedForms(false);
    };
  }, [queryForms]);

  const filters = React.useMemo(() => extractFilterValues(forms), [forms]);

  const renderLoading = () => (
    <PanelRow mods="u-textCenter">
      <div className="Loader-indicator">
        <span className="SpinAnimation" />
        <div className="Loader-indicatorText">
          Loading registration forms...
        </div>
      </div>
    </PanelRow>
  );

  const trimTextLength = (text: string) => text.substring(0, 70);

  const columns = [
    {
      label: 'Name',
      name: 'name',
      align: 'left',
      mods: 'u-size6of24 u-textEllipsis u-textNoWrap',
      isSortable: true,
      render: (_, { id, name, isReg2, programSeason: { divisionId } }) => (
        <div data-testid={'RegistrationList-name'}>
          <PopupTooltip
            mods="u-block u-textEllipsis u-textNoWrap"
            text={name}
            textHighlightColor="u-colorNeutral9"
            ariaDescribeBy={name}
          >
            {isReg2 ? (
              <Link to={`/organizations/${orgId}/registration/${id}/details`}>
                <span>{trimTextLength(name)}</span>
              </Link>
            ) : (
              <a href={generateFormUrl(id, divisionId, retiredStatus)}>
                {trimTextLength(name)}
              </a>
            )}
          </PopupTooltip>
          {!isReg2 && isMixedForms && (
            <div className="u-spaceTopSm">
              <Tag
                mods="u-bgForeground u-border u-borderRadiusMd u-borderWarning5 u-colorWarning5 u-padEndsXs u-PadSidesXs"
                text="Classic"
              />
            </div>
          )}
        </div>
      ),
    },
    {
      label: 'Status',
      name: 'status',
      align: 'left',
      mods: 'u-spaceLeftLg u-size3of24',
      isSortable: true,
      render: (_, { isOpen }) => (
        <div>
          {isOpen !== true ? (
            <div>
              <span className="close-status"></span>Closed
            </div>
          ) : (
            <div>
              <span className="open-status"></span>Open
            </div>
          )}
        </div>
      ),
    },
    {
      label: 'Date Created',
      name: 'dateCreated',
      align: 'left',
      mods: 'u-spaceLeftLg u-size3of24',
      isSortable: true,
      render: (_, { dateCreated: createdAt }) =>
        DateService.dateToString(
          DateService.stringToDate(
            createdAt,
            DateFormat.DATE_WITH_TIME_AND_TIMEZONE
          ),
          DateFormat.DD_MM_YYYY
        ),
    },
    {
      label: 'Program',
      name: 'programSeason',
      align: 'left',
      mods: 'u-size4of24 u-textEllipsis u-textNoWrap',
      isSortable: true,
      render: (_, row) => {
        const { programSeason } = row;
        const programSeasonName = `${programSeason.programName} (${programSeason.seasonName})`;

        return (
          <PopupTooltip
            mods="u-block u-textEllipsis u-textNoWrap"
            textHighlightColor="u-colorNeutral9"
            text={programSeasonName}
            ariaDescribeBy={programSeasonName}
          >
            {trimTextLength(programSeasonName)}
          </PopupTooltip>
        );
      },
    },
    {
      label: 'Signups',
      name: 'participantCount',
      align: 'right',
      mods: 'u-size2of24',
      isSortable: true,
    },
    {
      name: 'registeredToday',
      isSortable: false,
      mods: 'u-size2of24 u-textNoWrap',
      align: 'left',
      render: (_, { registeredTodayCount }) => (
        <div className={'signup-cell-container'}>
          {parseInt(registeredTodayCount) > 0 && (
            <Status state={'success'} text={`+${registeredTodayCount} today`} />
          )}
        </div>
      ),
    },
    {
      label: 'Collected',
      name: 'collected',
      align: 'right',
      mods: 'u-size2of24',
      isSortable: true,
    },
    {
      label: 'Balance',
      name: 'balance',
      align: 'right',
      mods: 'u-size2of24',
      isSortable: true,
    },
    {
      name: 'actions',
      isSortable: false,
      mods: 'u-size2of24',
      align: 'center',
      render: (_, { id, isReg2, isRetired }) => {
        const actions = getActions(
          id,
          orgId,
          isReg2,
          isRetired,
          history,
          setIsModalOpen,
          setFormIdForSharing,
          setIsShareDrawerOpen,
          updateRetired,
          setIsDuplicateModalOpen,
          setFocusedFormId
        );
        if (isReg2 && duplicateReg2FormsEnabled) {
          // We need to add the duplicate action for reg2 forms but we need to also make sure that it
          // is the middle action (#2) per design requirements
          actions.splice(1, 0, {
            label: 'Duplicate',
            onClick: () => {
              setDuplicateReg2FormId(id);
            },
          });
        }

        return (
          <ActionColumn
            testId={'registration-list-actions-component'}
            actions={actions}
          />
        );
      },
    },
  ];

  const loadSearchData = ({ page, itemsPerPage, filter, sortBy, sortAsc }) => {
    const filteredForms = applyFilters(forms, retiredStatus, filter);
    const sortedFilteredForms = applySort(filteredForms, sortBy, sortAsc);

    const startIndex = itemsPerPage * page - itemsPerPage;
    const endIndex = Math.min(
      sortedFilteredForms?.length,
      startIndex + itemsPerPage
    );

    const paginatedFormData = sortedFilteredForms?.slice(startIndex, endIndex);

    filteredFormsRef.current = sortedFilteredForms;

    return Promise.resolve({
      data: paginatedFormData,
      totalItems: sortedFilteredForms?.length,
    });
  };

  const mapData = (
    {
      name,
      season,
      id,
      isOpen,
      isRetired,
      isReg2,
      createdAt,
      participantCount,
      registeredTodayCount,
      collected,
      balance,
    }: Form,
    index: number
  ) => ({
    id: id,
    index: index,
    name: name,
    isOpen,
    isRetired,
    isReg2,
    dateCreated: createdAt,
    programSeason: {
      programId: season?.program?.id,
      programName: season?.program?.name,
      seasonName: season?.name,
      divisionId: season?.tsDivisionId,
    },
    participantCount,
    registeredTodayCount,
    collected: `$${Number(collected || 0).toFixed(2)}`,
    balance: `$${Number(balance || 0).toFixed(2)}`,
  });

  if (queryForms.loading) {
    return renderLoading();
  }

  if (queryForms.error) {
    return (
      <div>
        Unable to load your registration forms. Please try refreshing the page.
      </div>
    );
  }

  const focusedForm = forms
    ? forms.find((form) => form.id === focusedFormId)
    : null;

  return (
    <div
      data-testid="RegistrationList--container"
      className="registration-list__container"
    >
      <FilterContext.Provider value={{ activeFilters, setActiveFilters }}>
        <PaginatedTable
          loadData={loadSearchData}
          mapDataToRow={mapData}
          columns={columns}
          hideRowsSelect
          includeBasicSearch
          paginationPlacement={Placement.Bottom}
          searchPlaceholder="Search forms by name or program"
          noResultsText={`No ${retiredStatus} registration forms for this organization currently exist.`}
          onExport={({ exportToCsv }) =>
            handleExport(exportToCsv, filteredFormsRef.current, orgName)
          }
          filters={[
            PaginatedTable.SelectFilter('program', 'Program', filters.programs),
            PaginatedTable.SelectFilter('season', 'Season', filters.seasons),
            PaginatedTable.SelectFilter('status', 'Status', filters.status),
          ]}
          reloadDependency={forms}
          useExternalFilterProvider={programId && seasonId ? true : false}
        />

        {focusedForm && isDuplicateModalOpen ? (
          <FormModal
            type={FormOperation.Duplicate}
            orgId={orgId}
            show={!!(focusedForm && isDuplicateModalOpen)}
            onSubmit={({ program, season, newFormName }) => {
              duplicateForm(season, newFormName);
            }}
            onCancel={() => {
              setFocusedFormId(null);
              setIsDuplicateModalOpen(false);
            }}
            heading="Duplicate Form"
            subheading={
              <p className="help-text">
                Select where a copy of <em>{focusedForm?.name}</em> should
                appear.
              </p>
            }
            submitButtonText="Duplicate"
            testId="duplicate-form-modal"
          />
        ) : null}

        <ShareFormDrawer
          formIDForSharing={formIDForSharing}
          setFormIdForSharing={(id) => setFormIdForSharing(id)}
          isShareDrawerOpen={isShareDrawerOpen}
          setIsShareDrawerOpen={(bool) => setIsShareDrawerOpen(bool)}
          orgId={+orgId}
        />
      </FilterContext.Provider>

      {duplicateReg2FormId && (
        <DuplicateReg2FormModal
          orgId={orgId}
          formId={duplicateReg2FormId}
          onClose={(completed = false) => {
            setDuplicateReg2FormId(undefined);

            if (setIsModalOpen && completed) {
              setIsModalOpen(true);
            }
          }}
        />
      )}
    </div>
  );
};

function getActions(
  id: number,
  orgId: number,
  isReg2: boolean,
  isRetired: boolean,
  history: ReturnType<typeof useHistory>,
  setIsModalOpen: (boolean) => void,
  setFormIdForSharing: (id: number) => void,
  setIsShareDrawerOpen: (boolean) => void,
  updateRetired: (id: number, retired: boolean) => void,
  setIsDuplicateModalOpen: (boolean) => void,
  setFocusedFormId: (id: number) => void
): ActionType[] {
  const actions: ActionType[] = [];
  if (isReg2 && !isRetired) {
    actions.push(
      {
        label: 'Edit',
        onClick: () => {
          history.push(`/organizations/${orgId}/registration/${id}`, {
            prevPath: history.location.pathname,
          });

          if (setIsModalOpen) {
            setIsModalOpen(true);
          }
        },
      },
      {
        label: 'Share',
        onClick: () => {
          setFormIdForSharing(id);
          setIsShareDrawerOpen(true);
        },
      },
      {
        label: 'Retire',
        onClick: () => {
          updateRetired(id, true);
        },
      }
    );
  } else if (isRetired) {
    actions.push({
      label: 'Unretire',
      onClick: () => {
        updateRetired(id, false);
      },
    });
  } else {
    actions.push({
      label: 'Duplicate',
      onClick: () => {
        setFocusedFormId(id);
        setIsDuplicateModalOpen(true);
      },
    });
  }

  return actions;
}

export default RegistrationList;
